import * as React from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  InputAdornment,
  Badge,
  Menu,
  ListItemIcon,
  ListItemText,
  Checkbox,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  EnvelopeSimple as EnvelopeIcon,
  Eye as EyeIcon,
  House as HouseIcon,
  Phone as PhoneIcon,
  UsersThree as UsersThreeIcon,
  Funnel as FunnelIcon,
  SortAscending as SortIcon,
  MagnifyingGlass as SearchIcon,
  X as XIcon,
  Columns as ColumnsIcon,
  DownloadSimple as DownloadIcon,
  FileCsv as CsvIcon,
  FileXls as ExcelIcon,
  EyeSlash as EyeSlashIcon,
} from '@phosphor-icons/react/dist/ssr';
import { useMutation } from '@tanstack/react-query';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { CommunityRole } from '@/types/v2/community-role';
import { type User } from '@/types/v2/user';
import { paths } from '@/paths';
import axios from '@/lib/v2/axios';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';
import { RouterLink } from '@/components/core/v2/link';

import { CreateOrEditResidentModal } from './create-or-edit-resident-modal';
import { COMMUNITY_ROLES_LABELS } from '@/constants/community-roles';
import { getInitials } from '@/utils/get-initials';

interface PaginationMeta {
  per_page: number;
  current_page: number;
  has_more: boolean;
  next_page: string | null;
  prev_page: string | null;
  total: number;
  total_pages: number;
}

interface ResidentsTableProps {
  rows: User[];
  loading: boolean;
  communityId: string | number;
  onCreated: (user: User) => void;
}

export function ResidentsTable({ rows, loading, communityId, onCreated }: ResidentsTableProps): React.JSX.Element {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [columnVisibilityAnchor, setColumnVisibilityAnchor] = React.useState<null | HTMLElement>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [hiddenColumns, setHiddenColumns] = React.useState<Set<string>>(new Set());
  
  console.log('rows', rows);

  const resendInvitationMutation = useMutation({
    mutationFn: async (payload: { email: string; communityId: string | number }) => {
      const response = await axios.post('/api/v1/invitations/resend-community', {
        email: payload.email,
        community_id: String(payload.communityId),
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Invitation resent successfully');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to resend invitation';
      toast.error(message);
    },
  });

  const columns = React.useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: isMobile ? 200 : 280,
        accessorFn: (row) => `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || row.email,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search names...',
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                fontSize: '0.875rem',
                backgroundColor: 'primary.main',
                color: 'primary.contrastText'
              }}
            >
              {String(row.original.first_name?.[0]?.toUpperCase() || row.original.email?.[0]?.toUpperCase() || 'U')}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="body2">
                {row.original.first_name && row.original.last_name 
                  ? `${row.original.first_name} ${row.original.last_name}`
                  : row.original.email
                }
              </Typography>
              {row.original.first_name && row.original.last_name ? <Typography color="text.secondary" variant="caption">
                  {row.original.email}
                </Typography> : null}
            </Box>
          </Box>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: isMobile ? 200 : 250,
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search emails...',
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        size: isMobile ? 120 : 150,
        accessorFn: (row) => row.phone || 'No phone',
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search phones...',
        Cell: ({ row }) => (
          <Typography color={row.original.phone ? 'text.primary' : 'text.secondary'} variant="body2">
            {row.original.phone || 'No phone'}
          </Typography>
        ),
      },
      {
        accessorKey: 'units',
        header: 'Units',
        size: isMobile ? 150 : 200,
        accessorFn: (row) => {
          if (!row.units || row.units.length === 0) return 'No units assigned';
          return row.units.map((unit) => (unit.unit_number ? `#${unit.unit_number}` : unit.address)).join(', ');
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search units...',
        Cell: ({ row }) => {
          if (!row.original.units || row.original.units.length === 0) {
            return (
              <Chip 
                color="default" 
                icon={<HouseIcon size={16} />} 
                label="No units" 
                size="small"
                variant="outlined"
              />
            );
          }
          return (
            <Stack direction="row" flexWrap="wrap" gap={0.5} spacing={0.5}>
              {row.original.units.map((unit, index) => (
                <Chip
                  color="primary"
                  icon={<HouseIcon size={16} />}
                  key={unit.id}
                  label={unit.unit_number ? `#${unit.unit_number}` : unit.address}
                  size="small"
                  sx={{ maxWidth: 120 }}
                  variant="outlined"
                />
              ))}
            </Stack>
          );
        },
      },
      {
        accessorKey: 'community_roles',
        header: 'Role',
        size: 150,
        accessorFn: (row: any) => {
          if (!row.community_roles || !Array.isArray(row.community_roles)) return 'Member';
          const names: string[] = row.community_roles.map((r: any) => r.name);
          if (names.includes('community_manager')) return 'Manager';
          if (names.includes('board_member')) return 'Board Member';
          return 'Member';
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'select',
        filterSelectOptions: [
          { text: 'All', value: '' },
          { text: 'Member', value: 'Member' },
          { text: 'Manager', value: 'Manager' },
          { text: 'Board Member', value: 'Board Member' },
        ],
        Cell: ({ row }) => {
          const roles: string[] = Array.isArray(row.original.community_roles)
            ? row.original.community_roles.map((r: any) => r.name)
            : [];
          const isManager     = roles.includes('community_manager');
          const isBoardMember = roles.includes('board_member');

          if (isManager) {
            return (
              <Chip
                color="primary"
                icon={<UsersThreeIcon size={14} />}
                label="Manager"
                size="small"
                variant="soft"
              />
            );
          }
          if (isBoardMember) {
            return (
              <Chip
                color="secondary"
                label="Board Member"
                size="small"
                variant="soft"
              />
            );
          }
          return <Chip color="default" label="Member" size="small" variant="outlined" />;
        },
      },
      {
        accessorKey: 'community_title',
        header: 'Title',
        size: 120,
        accessorFn: (row: any) => {
          return row.community_title;
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'text',
        filterPlaceholder: 'Search titles...',
        Cell: ({ row }) => {
          const titleText = row.original.community_title;
          if (!titleText) return <Chip color="default" label="No Title" size="small" variant="outlined" />;
          return <Chip color="primary" label={`${titleText}`} size="small" variant="outlined" />;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        accessorFn: (row) => {
          const verified = row.email_verified_at ? 'Verified' : 'Unverified';
          return toTitleCase(verified);
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: 'select',
        filterSelectOptions: [
          { text: 'All', value: '' },
          { text: 'Verified Only', value: 'Verified' },
          { text: 'Unverified Only', value: 'Unverified' },
        ],
        Cell: ({ row }) => {
          const status = row.original.email_verified_at ? 'Verified' : 'Unverified';

          return (
            <Chip
              color={status === 'Verified' ? 'success' : 'warning'}
              icon={status === 'Verified' ? undefined : <EnvelopeIcon size={16} />}
              label={status}
              size="small"
              variant="outlined"
            />
          );
        },
      },
    ],
    [isMobile]
  );


  // Export functions
  const exportToCsv = () => {
    const visibleColumns = columns.filter(col => !hiddenColumns.has(col.accessorKey as string));
    const headers = visibleColumns.map(col => col.header).join(',');
    
    const csvData = rows.map(row => {
      return visibleColumns.map(col => {
        if (col.accessorKey === 'name') {
          return `"${row.first_name || ''} ${row.last_name || ''}".trim() || row.email`;
        }
        if (col.accessorKey === 'units') {
          if (!row.units || row.units.length === 0) return 'No units assigned';
          return `"${row.units.map((unit: any) => unit.unit_number ? `#${unit.unit_number}` : unit.address).join(', ')}"`;
        }
        if (col.accessorKey === 'community_roles') {
          const names = row.community_roles?.map((r: any) => r.name) ?? [];
          if (names.includes('community_manager')) return 'Manager';
          if (names.includes('board_member')) return 'Board Member';
          return 'Member';
        }
        if (col.accessorKey === 'status') {
          return row.email_verified_at ? 'Verified' : 'Unverified';
        }
        return row[col.accessorKey as keyof User] || '';
      }).join(',');
    }).join('\n');
    
    const csvContent = `${headers}\n${csvData}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `community-users-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    // For now, we'll use CSV format but with .xls extension
    // In a real app, you'd use a library like xlsx to create actual Excel files
    exportToCsv();
  };

  const toggleColumnVisibility = (columnKey: string) => {
    const newHiddenColumns = new Set(hiddenColumns);
    if (newHiddenColumns.has(columnKey)) {
      newHiddenColumns.delete(columnKey);
    } else {
      newHiddenColumns.add(columnKey);
    }
    setHiddenColumns(newHiddenColumns);
  };



  return (
    <React.Fragment>
      <MaterialReactTable 
          columns={columns.filter(col => !hiddenColumns.has(col.accessorKey as string))}
          data={rows}
          enableColumnFilters
          enableColumnResizing
          enableDensityToggle
          enableFullScreenToggle
          enableMultiRowSelection
          enablePagination
          enableRowActions={false}
          enableRowSelection
          enableSelectAll
          enableSorting
          initialState={{
            showGlobalFilter: true,
            showColumnFilters: false,
            columnPinning: {
              left: ['mrt-row-expand', 'mrt-row-select'],
              right: ['mrt-row-actions'],
            },
            sorting: [
              { id: 'name', desc: false },
            ],
          }}
          muiSearchTextFieldProps={{
            size: 'small',
            variant: 'outlined',
            placeholder: 'Search all users...',
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={20} />
                </InputAdornment>
              ),
            },
          }}
          muiTableContainerProps={(props) => ({
            ...props,
            sx: { minHeight: '500px', maxWidth: '100%', overflowX: 'auto' },
          })}
          muiTablePaperProps={{ elevation: 0, sx: { border: 'none', borderRadius: 0 } }}
          muiTableProps={{ sx: { tableLayout: 'fixed' } }}
          muiTableHeadProps={{
            sx: {
              '& tr th': {
                bgcolor: 'background.default',
                borderBottom: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary',
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                py: 1.5,
              },
            },
          }}
          muiTableBodyProps={{
            sx: {
              '& tr td': {
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                fontSize: '0.875rem',
                py: 1.5,
              },
            },
          }}
          muiBottomToolbarProps={{
            sx: {
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              boxShadow: 'none',
            },
          }}
          muiTableBodyRowProps={({ row }: { row: any }) => ({
            onClick: () => {
              if (row.original.uuid) navigate(paths.users.details(row.original.uuid));
            },
            sx: {
              cursor: row.original.uuid ? 'pointer' : 'default',
              '&:hover td': { bgcolor: row.original.uuid ? 'action.hover' : undefined },
            },
          })}
          renderTopToolbar={({ table }) => {
            const verifiedCount = rows.filter((u) => u.email_verified_at).length;
            const withUnitsCount = rows.filter((u) => u.units && u.units.length > 0).length;

            return (
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <MRT_GlobalFilterTextField table={table} />
                  <MRT_ToggleFiltersButton table={table} />
                  <MRT_ShowHideColumnsButton table={table} />
                  <MRT_ToggleDensePaddingButton table={table} />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      ml: 1,
                      pl: 1.5,
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography color="text.disabled" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {verifiedCount} verified
                    </Typography>
                    <Typography color="text.disabled" sx={{ fontSize: '0.6875rem' }}>·</Typography>
                    <Typography color="text.disabled" sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {withUnitsCount} with units
                    </Typography>
                  </Box>
                </Stack>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    onClick={(e) => { setColumnVisibilityAnchor(e.currentTarget); }}
                    size="small"
                    startIcon={<ColumnsIcon size={16} />}
                    sx={{ fontWeight: 500 }}
                    variant="outlined"
                  >
                    Columns
                  </Button>
                  <Button
                    onClick={(e) => { setExportMenuAnchor(e.currentTarget); }}
                    size="small"
                    startIcon={<DownloadIcon size={16} />}
                    sx={{ fontWeight: 500 }}
                    variant="outlined"
                  >
                    Export
                  </Button>
                </Box>
              </Box>
            );
          }}
          state={{
            isLoading: loading,
          }}
        />

      {/* Column Visibility Menu */}
      <Menu
        PaperProps={{
          sx: {
            minWidth: 250,
            maxHeight: 400,
            overflow: 'auto',
          },
        }}
        anchorEl={columnVisibilityAnchor}
        onClose={() => { setColumnVisibilityAnchor(null); }}
        open={Boolean(columnVisibilityAnchor)}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="subtitle2">
            Column Visibility
          </Typography>
          <Typography color="text.secondary" variant="caption">
            Select which columns to display
          </Typography>
        </Box>
        {columns.map((column) => (
          <MenuItem key={column.accessorKey} onClick={() => { toggleColumnVisibility(column.accessorKey as string); }}>
            <ListItemIcon>
              <Checkbox
                checked={!hiddenColumns.has(column.accessorKey as string)}
                size="small"
              />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2">
                {column.header}
              </Typography>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Export Menu */}
      <Menu
        PaperProps={{
          sx: {
            minWidth: 200,
          },
        }}
        anchorEl={exportMenuAnchor}
        onClose={() => { setExportMenuAnchor(null); }}
        open={Boolean(exportMenuAnchor)}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="subtitle2">
            Export Data
          </Typography>
          <Typography color="text.secondary" variant="caption">
            Choose export format
          </Typography>
        </Box>
        <MenuItem onClick={() => { exportToCsv(); setExportMenuAnchor(null); }}>
          <ListItemIcon>
            <CsvIcon size={20} />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Export to CSV</Typography>
            <Typography color="text.secondary" variant="caption">
              Download as .csv file
            </Typography>
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { exportToExcel(); setExportMenuAnchor(null); }}>
          <ListItemIcon>
            <ExcelIcon size={20} />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2">Export to Excel</Typography>
            <Typography color="text.secondary" variant="caption">
              Download as .xls file
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

    </React.Fragment>
  );
}
